import { useState } from 'react'
import { api } from '../lib/api'
import { ThemeToggle } from '../App'

type Stage = 'email' | 'pin'

export function Login({
  onSuccess,
  theme,
  onToggleTheme
}: {
  onSuccess: () => void
  theme: 'light' | 'dark'
  onToggleTheme: () => void
}) {
  const [stage, setStage] = useState<Stage>('email')
  const [email, setEmail] = useState('')
  const [pin, setPin] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await api.requestPin(email)
      setInfo('If your email is approved, a 6-digit code is on its way.')
      setStage('pin')
    } catch (err) {
      setError(errMsg(err))
    } finally {
      setLoading(false)
    }
  }

  async function submitPin(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await api.verifyPin(email, pin)
      onSuccess()
    } catch (err) {
      setError(errMsg(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-full items-center justify-center p-6">
      <div className="absolute right-4 top-4">
        <ThemeToggle theme={theme} onToggle={onToggleTheme} />
      </div>
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-2xl backdrop-blur dark:border-slate-800 dark:bg-slate-900/60">
        <h1 className="text-2xl font-semibold">SharedMailbox Viewer</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Sign in with your work email.</p>

        {stage === 'email' && (
          <form onSubmit={submitEmail} className="mt-6 space-y-4">
            <label className="block">
              <span className="text-sm text-slate-700 dark:text-slate-300">Email</span>
              <input
                type="email"
                autoFocus
                required
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-sky-500 focus:outline-none dark:border-slate-700 dark:bg-slate-950"
                placeholder="you@company.com"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50"
            >
              {loading ? 'Sending…' : 'Send code'}
            </button>
          </form>
        )}

        {stage === 'pin' && (
          <form onSubmit={submitPin} className="mt-6 space-y-4">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Code sent to <span className="text-slate-900 dark:text-slate-200">{email}</span>
            </div>
            <label className="block">
              <span className="text-sm text-slate-700 dark:text-slate-300">6-digit code</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoFocus
                required
                value={pin}
                onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-center text-2xl tracking-[0.5em] focus:border-sky-500 focus:outline-none dark:border-slate-700 dark:bg-slate-950"
              />
            </label>
            <button
              type="submit"
              disabled={loading || pin.length !== 6}
              className="w-full rounded-md bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50"
            >
              {loading ? 'Verifying…' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={() => { setStage('email'); setPin(''); setError(null); setInfo(null) }}
              className="w-full text-xs text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Use a different email
            </button>
          </form>
        )}

        {info && <p className="mt-4 text-xs text-slate-600 dark:text-slate-400">{info}</p>}
        {error && <p className="mt-4 text-xs text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </div>
  )
}

function errMsg(err: unknown): string {
  const code = err instanceof Error ? err.message : 'unknown'
  const table: Record<string, string> = {
    invalid_email: 'That doesn\'t look like a valid email.',
    invalid_pin: 'That code is wrong. Try again.',
    expired: 'That code expired. Request a new one.',
    too_many_attempts: 'Too many attempts. Request a new code.',
    rate_limited: 'Too many requests. Try again later.',
    send_failed: 'We couldn\'t send the code. Try again in a moment.'
  }
  return table[code] ?? 'Something went wrong.'
}
